'use client';

import React, { useState } from 'react';

interface Booking {
  id: number;
  ref: string;
  guest: string;
  host: string;
  property: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  amount: string;
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  guests: number;
}

const bookings: Booking[] = [
  { id: 1, ref: 'NDT-2026-0821', guest: 'Sarah Nakato', host: 'James Ssekandi', property: 'Luxury Villa Kololo', checkIn: 'Aug 25', checkOut: 'Aug 30', nights: 5, amount: 'UGX 2,500,000', status: 'confirmed', guests: 2 },
  { id: 2, ref: 'NDT-2026-0834', guest: 'Sarah Nakato', host: 'Grace Achieng', property: 'Lakeside Retreat Entebbe', checkIn: 'Sep 5', checkOut: 'Sep 8', nights: 3, amount: 'UGX 1,200,000', status: 'confirmed', guests: 3 },
  { id: 3, ref: 'NDT-2026-0901', guest: 'Moses Kato', host: 'Amina Hassan', property: 'City Apartment Kampala', checkIn: 'Sep 20', checkOut: 'Sep 22', nights: 2, amount: 'UGX 450,000', status: 'pending', guests: 1 },
  { id: 4, ref: 'NDT-2026-0712', guest: 'Sarah Nakato', host: 'David Ochieng', property: 'Safari Lodge Murchison', checkIn: 'Jul 10', checkOut: 'Jul 14', nights: 4, amount: 'UGX 3,200,000', status: 'completed', guests: 4 },
  { id: 5, ref: 'NDT-2026-0520', guest: 'Peter Mugisha', host: 'Ruth Kemigisha', property: 'Mountain Cabin Rwenzori', checkIn: 'May 20', checkOut: 'May 23', nights: 3, amount: 'UGX 900,000', status: 'completed', guests: 2 },
  { id: 6, ref: 'NDT-2026-0402', guest: 'Moses Kato', host: 'Amina Hassan', property: 'City Apartment Kampala', checkIn: 'Apr 2', checkOut: 'Apr 4', nights: 2, amount: 'UGX 450,000', status: 'cancelled', guests: 1 },
];

const statusColors: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-600',
};

export default function AdminBookings() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = bookings.filter((b) => {
    const matchFilter = filter === 'all' || b.status === filter;
    const matchSearch = b.ref.toLowerCase().includes(search.toLowerCase()) ||
      b.guest.toLowerCase().includes(search.toLowerCase()) ||
      b.property.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Bookings', value: '1,089', color: 'text-gray-900' },
          { label: 'Confirmed', value: '847', color: 'text-green-600' },
          { label: 'Pending', value: '124', color: 'text-amber-600' },
          { label: 'Cancelled', value: '118', color: 'text-red-500' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by ref, guest, or property..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'confirmed', 'pending', 'completed', 'cancelled'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${
                filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Booking Ref</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Guest</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Property</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Dates</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-xs font-mono font-semibold text-gray-700">{b.ref}</td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-gray-900">{b.guest}</p>
                    <p className="text-xs text-gray-400">Host: {b.host}</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">{b.property}</td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-gray-700">{b.checkIn} – {b.checkOut}</p>
                    <p className="text-xs text-gray-400">{b.nights} nights · {b.guests} guest{b.guests > 1 ? 's' : ''}</p>
                  </td>
                  <td className="px-5 py-4 text-sm font-bold text-gray-900">{b.amount}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[b.status]}`}>{b.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
